import React, { useCallback, useMemo, useRef } from 'react';
import { CrewAssignment, ScheduleEvent, OOOEntry, EventColorEntry } from './types';
import useScheduleStore, { DayBar, BlockBar } from '../stores/useScheduleStore';
import { OOO_STYLE, UNAVAILABLE_STYLE, ROW_GLOW_CLASSES, COL_GLOW_CLASSES, ROW_COL_INTERSECT_CLASSES, PENDING_EVENT_COLOR } from './schedule-colors';

interface ScheduleCellProps {
  dateStr: string;
  userId: string;
  /** Pre-merged per-day bars for this user × date (includes pending state). */
  bars: DayBar[];
  /** Scheduler-authored block bar for this cell, if any. */
  blockBar?: BlockBar | null;
  eventsOnDay: ScheduleEvent[];
  oooEntries: OOOEntry[];
  eventColorMap: Map<string, EventColorEntry>;
  eventLookup: Map<string, ScheduleEvent>;
  isWeekend: boolean;
  isToday: boolean;
  /** Crew view (items 7+14): render the same grid with every write path off.
   *  Guards the cell's click and drop AND `draggable`, which the two edge
   *  handles already key off — so one flag closes all four. */
  readOnly?: boolean;
  isRowHovered: boolean;
  isColHovered: boolean;
  onCellHover: (userId: string | null, date: string | null) => void;
  onCellClick: (userId: string, dateStr: string, rect: DOMRect, eventId?: string) => void;
  onDragStart: (e: React.DragEvent, assignment: CrewAssignment, userId: string, shiftRange?: { start: string; end: string }) => void;
  onDrop: (e: React.DragEvent, targetUserId: string, targetDateStr: string) => void;
  onDragOver: (e: React.DragEvent) => void;
}

const ScheduleCell: React.FC<ScheduleCellProps> = React.memo(({
  dateStr, userId, bars, blockBar,
  oooEntries, eventColorMap, eventLookup, isWeekend, isToday, readOnly = false,
  isRowHovered, isColHovered,
  onCellHover, onCellClick, onDragStart, onDrop, onDragOver,
}) => {
  const cellRef = useRef<HTMLDivElement>(null);
  const hasOOO = oooEntries.length > 0;
  const isTentative = hasOOO && oooEntries.every(o => o.is_tentative);

  // Gated selector: only cells in the dragging user's row subscribe to drag
  // updates. Cells outside the active row see `null` and skip re-render on
  // each pointermove (~60Hz). Zustand bails out on referential equality.
  const drag = useScheduleStore(s => (s.drag && s.drag.userId === userId ? s.drag : null));
  const startDrag = useScheduleStore(s => s.startDrag);
  const addPendingBlockChange = useScheduleStore(s => s.addPendingBlockChange);

  const visibleBars = bars;

  const handleClick = useCallback((e?: React.MouseEvent | React.KeyboardEvent) => {
    const rect = cellRef.current?.getBoundingClientRect();
    if (!rect) return;

    // Shift+click power-user fast path: emit a pending-add block for this
    // (user, day) directly, bypassing the popover. The dashed-border hatched
    // bar appears immediately; subsequent drag-extend widens the range.
    if (e && 'shiftKey' in e && e.shiftKey && !hasOOO && !blockBar) {
      addPendingBlockChange({
        id: `block-shift-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        action: 'add',
        user_id: userId,
        date: dateStr,
        notes: '',
      });
      return;
    }

    if (hasOOO) {
      onCellClick(userId, dateStr, rect, '__ooo__');
    } else if (blockBar) {
      onCellClick(userId, dateStr, rect, '__block__');
    } else if (visibleBars.length > 0) {
      onCellClick(userId, dateStr, rect, visibleBars[0].event_id);
    } else {
      onCellClick(userId, dateStr, rect);
    }
  }, [userId, dateStr, hasOOO, blockBar, visibleBars, onCellClick, addPendingBlockChange]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e);
    }
  }, [handleClick]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    onDrop(e, userId, dateStr);
  }, [onDrop, userId, dateStr]);

  // Suppress the cell's right border when any visible bar continues into the
  // next cell — otherwise the 1px tick interrupts the continuous-bar visual.
  // OOO cells always keep their border (no bars to bridge).
  const hasContinuingShiftRight = !hasOOO && visibleBars.some(b => b.shiftEnd > dateStr);
  const hasContinuingBlockRight = !hasOOO && !!blockBar && blockBar.blockEnd > dateStr;
  const borderClass = (hasContinuingShiftRight || hasContinuingBlockRight) ? 'border-b' : 'border-r border-b';

  const bgClass = isToday ? 'bg-primary/5 day:bg-amber-50' : isWeekend ? 'bg-gray-800/40 day:bg-gray-100' : '';
  const glowClass = isRowHovered && isColHovered
    ? ROW_COL_INTERSECT_CLASSES
    : isRowHovered
      ? ROW_GLOW_CLASSES
      : isColHovered
        ? COL_GLOW_CLASSES
        : '';

  const handleMouseEnter = useCallback(() => onCellHover(userId, dateStr), [onCellHover, userId, dateStr]);
  const handleMouseLeave = useCallback(() => onCellHover(null, null), [onCellHover]);
  const label = hasOOO
    ? `${dateStr}, out of office`
    : `${dateStr}, ${visibleBars.length} assignment${visibleBars.length !== 1 ? 's' : ''}`;

  // Drag-preview ghost: if a drag is active for the matching user+event and
  // this cell's date falls in the preview range BUT is outside the original
  // shift range, render a ghost bar to show the planned extension.
  // (The cell is already gated to render this only if `drag.userId === userId`
  // via the store selector — `drag` is null in other rows.)
  const previewBars = useMemo(() => {
    if (!drag || drag.invalid || drag.kind === 'block') return [];
    const lo = drag.previewDate < drag.anchorDate ? drag.previewDate : drag.anchorDate;
    const hi = drag.previewDate < drag.anchorDate ? drag.anchorDate : drag.previewDate;
    if (dateStr < lo || dateStr > hi) return [];
    if (dateStr >= drag.originalStart && dateStr <= drag.originalEnd) return [];
    const event = eventLookup.get(drag.eventId);
    if (!event) return [];
    return [{ event_id: drag.eventId, role: '', event }];
  }, [drag, dateStr, eventLookup]);

  // Block-drag preview: parallel to event drag preview, but for unavailability
  // block drags. Renders as a hatched ghost on extension cells.
  const blockPreviewActive = useMemo(() => {
    if (!drag || drag.invalid || drag.kind !== 'block') return false;
    const lo = drag.previewDate < drag.anchorDate ? drag.previewDate : drag.anchorDate;
    const hi = drag.previewDate < drag.anchorDate ? drag.anchorDate : drag.previewDate;
    if (dateStr < lo || dateStr > hi) return false;
    if (dateStr >= drag.originalStart && dateStr <= drag.originalEnd) return false;
    return true;
  }, [drag, dateStr]);

  // Block shrink-preview: if dragging a pending-add block and this cell was
  // in the original range but is now outside the preview, mark it for visual
  // removal. The drag commits per-day add/remove on pointerup.
  const blockIsShrinkPreview = useMemo(() => {
    if (!blockBar || !drag || drag.invalid || drag.kind !== 'block') return false;
    if (drag.userId !== userId) return false;
    const lo = drag.previewDate < drag.anchorDate ? drag.previewDate : drag.anchorDate;
    const hi = drag.previewDate < drag.anchorDate ? drag.anchorDate : drag.previewDate;
    return (
      dateStr >= drag.originalStart &&
      dateStr <= drag.originalEnd &&
      (dateStr < lo || dateStr > hi)
    );
  }, [blockBar, drag, userId, dateStr]);

  return (
    <div
      ref={cellRef}
      role="gridcell"
      tabIndex={readOnly ? -1 : 0}
      aria-label={label}
      data-date={dateStr}
      data-user-id={userId}
      className={`relative min-h-[32px] ${borderClass} border-gray-800/60 day:border-gray-200 px-0.5 py-0.5 cursor-pointer
        hover:bg-gray-700/20 day:hover:bg-gray-100 transition-colors outline-none focus:ring-1 focus:ring-primary/30 ${bgClass} ${glowClass}`}
      onClick={readOnly ? undefined : handleClick}
      onKeyDown={handleKeyDown}
      onDragOver={readOnly ? undefined : onDragOver}
      onDrop={readOnly ? undefined : handleDrop}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      {hasOOO && (
        <div className={`rounded px-1 py-0.5 text-[10px] font-medium text-center truncate
          ${isTentative ? `${OOO_STYLE.tentativeBg} ${OOO_STYLE.tentativeText}` : `${OOO_STYLE.bg} ${OOO_STYLE.text}`}
          border ${OOO_STYLE.border}`}>
          {isTentative ? 'OOO?' : 'OOO'}
        </div>
      )}

      {!hasOOO && blockBar && (() => {
        const isAtLeftEdge = dateStr === blockBar.blockStart;
        const isAtRightEdge = dateStr === blockBar.blockEnd;
        const continuesLeft = !isAtLeftEdge;
        const continuesRight = !isAtRightEdge;
        // Drag handles only on pending-add bars in v1 — saved blocks must be
        // deleted (via popover) and re-created to change their range. Avoids
        // the GiST-vs-PATCH dance.
        // !readOnly matters: this keyed off isPendingAdd alone, so the crew view
        // was safe only because it can never produce a pending block. That made the
        // "one flag closes all four write paths" claim above true by luck rather
        // than by construction.
        const showHandles = blockBar.isPendingAdd && !readOnly;
        const handleBlockPointerDown = (edge: 'left' | 'right') => (e: React.PointerEvent) => {
          if (!showHandles) return;
          e.stopPropagation();
          e.preventDefault();
          startDrag({
            userId,
            eventId: null,
            edge,
            anchorDate: edge === 'right' ? blockBar.blockStart : blockBar.blockEnd,
            originalStart: blockBar.blockStart,
            originalEnd: blockBar.blockEnd,
            previewDate: edge === 'right' ? blockBar.blockEnd : blockBar.blockStart,
            invalid: false,
            kind: 'block',
          });
        };

        return (
          <div className="relative group">
            <div
              aria-label={`Unavailable${blockBar.notes ? ` — ${blockBar.notes}` : ''}`}
              className={`-mx-0.5 px-1 py-0.5 text-[10px] font-medium truncate border
                ${UNAVAILABLE_STYLE.bg} ${UNAVAILABLE_STYLE.text} ${UNAVAILABLE_STYLE.border}
                ${UNAVAILABLE_STYLE.hatch}
                ${continuesLeft ? 'rounded-l-none border-l-0' : 'rounded-l'}
                ${continuesRight ? 'rounded-r-none border-r-0' : 'rounded-r'}
                ${blockBar.isPendingAdd ? 'border-dashed' : ''}
                ${(blockBar.isPendingRemove || blockIsShrinkPreview) ? 'opacity-40 line-through' : ''}`}
              title={blockBar.notes || 'Unavailable'}
            >
              {isAtLeftEdge ? (
                <span className="select-none">{blockBar.notes ? (blockBar.notes.length > 12 ? blockBar.notes.slice(0, 11) + '…' : blockBar.notes) : 'Unavailable'}</span>
              ) : (
                <span aria-hidden="true" className="inline-block min-h-[14px]" />
              )}
            </div>
            {isAtLeftEdge && showHandles && (
              <div
                role="slider"
                aria-label="Extend block start"
                onPointerDown={handleBlockPointerDown('left')}
                className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize
                  opacity-0 group-hover:opacity-100 bg-slate-500/80 day:bg-slate-500/80 rounded-l
                  transition-opacity"
              />
            )}
            {isAtRightEdge && showHandles && (
              <div
                role="slider"
                aria-label="Extend block end"
                onPointerDown={handleBlockPointerDown('right')}
                className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize
                  opacity-0 group-hover:opacity-100 bg-slate-500/80 day:bg-slate-500/80 rounded-r
                  transition-opacity"
              />
            )}
          </div>
        );
      })()}

      {!hasOOO && visibleBars.map((bar) => {
        const event = eventLookup.get(bar.event_id);
        const color = event?.status === 'pending' ? PENDING_EVENT_COLOR : eventColorMap.get(bar.event_id);
        const name = event?.name || '?';
        const roleSuffix = bar.role ? ` (${bar.role})` : '';
        const isAtLeftEdge = dateStr === bar.shiftStart;
        const isAtRightEdge = dateStr === bar.shiftEnd;
        const continuesLeft = !isAtLeftEdge;
        const continuesRight = !isAtRightEdge;
        const draggable = !readOnly && !bar.isPendingRemove;

        // Shrink preview: if a drag is in progress on THIS bar's shift and the
        // current cell's date is inside the original range but OUTSIDE the
        // current preview range, mark it as "being removed" visually until
        // pointerup commits the change.
        let isShrinkPreview = false;
        if (drag && !drag.invalid && drag.userId === userId && drag.eventId === bar.event_id) {
          const lo = drag.previewDate < drag.anchorDate ? drag.previewDate : drag.anchorDate;
          const hi = drag.previewDate < drag.anchorDate ? drag.anchorDate : drag.previewDate;
          if (
            dateStr >= drag.originalStart &&
            dateStr <= drag.originalEnd &&
            (dateStr < lo || dateStr > hi)
          ) {
            isShrinkPreview = true;
          }
        }

        const dragAssignment: CrewAssignment = {
          event_id: bar.event_id,
          user_id: userId,
          role: bar.role,
          notes: '',
        };

        const handlePointerDown = (edge: 'left' | 'right') => (e: React.PointerEvent) => {
          // Don't fire if the bar is in a pending-remove state — wouldn't make sense to extend it
          if (bar.isPendingRemove) return;
          e.stopPropagation();
          e.preventDefault();
          startDrag({
            userId,
            eventId: bar.event_id,
            edge,
            // The anchor is the edge that ISN'T moving
            anchorDate: edge === 'right' ? bar.shiftStart : bar.shiftEnd,
            originalStart: bar.shiftStart,
            originalEnd: bar.shiftEnd,
            previewDate: edge === 'right' ? bar.shiftEnd : bar.shiftStart,
            invalid: false,
          });
        };

        return (
          <div
            key={bar.event_id}
            className="relative group"
          >
            <div
              draggable={draggable}
              aria-roledescription="Draggable crew assignment"
              aria-label={`${name}${roleSuffix}. Drag to reassign, or drag edges to extend.`}
              onDragStart={(e) => onDragStart(e, dragAssignment, userId, { start: bar.shiftStart, end: bar.shiftEnd })}
              // -mx-0.5 cancels the cell's px-0.5 padding so adjacent cells'
              // segments touch. Rounded corners + borders only on the actual
              // shift edges — internal segments are square + borderless on
              // the joining side, giving the visual effect of one continuous
              // bar across consecutive days.
              className={`-mx-0.5 px-1 py-0.5 text-[10px] font-medium truncate border
                ${color?.bg || 'bg-surface-container-highest day:bg-gray-200'} ${color?.text || 'text-on-surface day:text-gray-700'} ${color?.border || 'border-outline-variant day:border-gray-300'}
                ${continuesLeft ? 'rounded-l-none border-l-0' : 'rounded-l'}
                ${continuesRight ? 'rounded-r-none border-r-0' : 'rounded-r'}
                ${bar.isPendingAdd ? 'border-dashed' : ''}
                ${bar.isPendingRemove || isShrinkPreview ? 'opacity-40 line-through' : ''}
                cursor-grab active:cursor-grabbing`}
              title={`${name}${roleSuffix}`}
            >
              {/* Event name only on the leftmost day of the shift — middle
                  and right segments are colored continuation. min-h preserves
                  the bar's height; using a sized empty span instead of &nbsp;
                  avoids screen readers reading "non-breaking space". */}
              {isAtLeftEdge ? (
                <>
                  {name.length > 12 ? name.slice(0, 11) + '...' : name}
                  {bar.role && <span className="opacity-60 ml-0.5">({bar.role})</span>}
                </>
              ) : (
                <span aria-hidden="true" className="inline-block min-h-[14px]" />
              )}
            </div>

            {/* Drag-extend handles. Only rendered at the edges of the shift —
                you can't grab the middle of a multi-day bar from any cell other
                than the first/last day. Hidden until hover via the parent
                `group` + `group-hover`. */}
            {isAtLeftEdge && draggable && (
              <div
                role="slider"
                aria-label="Extend shift start"
                onPointerDown={handlePointerDown('left')}
                className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize
                  opacity-0 group-hover:opacity-100 bg-primary/70 day:bg-amber-500/80 rounded-l
                  transition-opacity"
              />
            )}
            {isAtRightEdge && draggable && (
              <div
                role="slider"
                aria-label="Extend shift end"
                onPointerDown={handlePointerDown('right')}
                className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize
                  opacity-0 group-hover:opacity-100 bg-primary/70 day:bg-amber-500/80 rounded-r
                  transition-opacity"
              />
            )}
          </div>
        );
      })}

      {/* Block-drag preview ghost — hatched bar shown on cells in the preview
          extension range during an unavailability-block drag. */}
      {blockPreviewActive && (
        <div
          className={`-mx-0.5 px-1 py-0.5 text-[10px] font-medium truncate border border-dashed mt-0.5
            ${UNAVAILABLE_STYLE.bg} ${UNAVAILABLE_STYLE.text} ${UNAVAILABLE_STYLE.border}
            ${UNAVAILABLE_STYLE.hatch} opacity-60`}
          title={`Extend block to ${dateStr}`}
        >
          <span aria-hidden="true" className="inline-block min-h-[14px]" />
        </div>
      )}

      {/* Drag preview ghost — shown on cells in the preview extension range */}
      {previewBars.map((p) => {
        const color = p.event.status === 'pending' ? PENDING_EVENT_COLOR : eventColorMap.get(p.event_id);
        return (
          <div
            key={`preview-${p.event_id}`}
            className={`rounded px-1 py-0.5 text-[10px] font-medium truncate mt-0.5 border border-dashed
              ${color?.bg || 'bg-surface-container-highest day:bg-gray-200'} ${color?.text || 'text-on-surface day:text-gray-700'} ${color?.border || 'border-outline-variant day:border-gray-300'}
              opacity-60`}
            title={`Extend to ${dateStr}`}
          >
            {p.event.name.length > 12 ? p.event.name.slice(0, 11) + '...' : p.event.name}
          </div>
        );
      })}

      {/* Invalid-target indicator: if a drag is in progress for this user and
          this cell is the current pointer target but invalid, paint a red rail. */}
      {drag && drag.userId === userId && drag.previewDate === dateStr && drag.invalid && (
        <div className="absolute inset-0 pointer-events-none ring-2 ring-inset ring-red-500/70 rounded" />
      )}
    </div>
  );
});

ScheduleCell.displayName = 'ScheduleCell';
export default ScheduleCell;
